import type { Position, Transaction } from '@shared/types'

function etDate(ts: number): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ts))
}

const ET_WEEKDAY = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'short' })

// The US trading day rolls over at 04:00 ET (pre-market open) — NOT at midnight.
// From after-hours close (20:00 ET) until the next pre-market open (04:00 ET) the
// market is dead, so that overnight stretch still belongs to the session that just
// traded. Without this, just past midnight ET a stock you bought "today" suddenly
// looks like a prior-day holding and its day-P&L gets measured against yesterday's
// close instead of your entry. We also roll Sat/Sun back to Friday so a weekend
// view keeps the last session as "today".
const DAY_ROLLOVER_MS = 4 * 3_600_000

function tradingDayKey(ts: number): string {
  const shifted = ts - DAY_ROLLOVER_MS
  const wd = ET_WEEKDAY.format(new Date(shifted))
  const back = wd === 'Sat' ? 1 : wd === 'Sun' ? 2 : 0
  return etDate(back ? shifted - back * 86_400_000 : shifted)
}

/** Was this timestamp in the CURRENT US trading day? Uses a 04:00 ET rollover, so
 *  the overnight gap just after midnight ET still counts as the prior session's day.
 *  Pass `now` to make it deterministic/testable. */
export function isTodayET(ts: number, now: number = Date.now()): boolean {
  return tradingDayKey(ts) === tradingDayKey(now)
}

export interface TodayPL {
  gain: number
  pct: number
}

/**
 * Broker-correct "today's P&L": the reference for each position depends on when
 * it was opened.
 *  - Opened TODAY → today's gain = current value − cost (your intraday gain;
 *    you didn't own it at yesterday's close, so comparing to that is wrong).
 *  - Held from a PRIOR day → today's gain = current value − (shares-at-open ×
 *    yesterday's close), adjusted by any shares bought/sold today (cash-flow).
 */
export function portfolioTodayPL(
  positions: Position[],
  transactions: Transaction[],
  getPrice: (symbol: string) => number | null,
  getPrevClose: (symbol: string) => number | null,
  now: number = Date.now(),
): TodayPL {
  let gain = 0
  let base = 0
  for (const p of positions) {
    const cur = getPrice(p.symbol)
    if (cur == null) continue
    const curValue = p.quantity * cur

    if (isTodayET(p.createdAt, now)) {
      // entire position was opened today → today's gain is the full gain vs cost
      const cost = p.quantity * p.costBasis
      gain += curValue - cost
      base += cost
      continue
    }

    // held from before — start-of-day value vs yesterday's close, plus today's trades
    const yc = getPrevClose(p.symbol) ?? cur
    let netSharesToday = 0
    let cashInToday = 0
    for (const t of transactions) {
      if (t.symbol !== p.symbol || !isTodayET(t.createdAt, now)) continue
      if (t.side === 'buy') {
        netSharesToday += t.quantity
        cashInToday += t.quantity * t.price
      } else {
        netSharesToday -= t.quantity
        cashInToday -= t.quantity * t.price
      }
    }
    const startQty = Math.max(0, p.quantity - netSharesToday)
    const startValue = startQty * yc
    gain += curValue - startValue - cashInToday
    base += startValue + Math.max(0, cashInToday)
  }
  return { gain, pct: base > 0 ? (gain / base) * 100 : 0 }
}
